import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { format } from "date-fns";
import {
  PhoneIcon,
  CalendarDaysIcon,
  ClockIcon,
  FlagIcon,
  FaceSmileIcon,
  EnvelopeIcon,
  XMarkIcon,
  CheckCircleIcon,
  UserIcon,
  ClipboardIcon,
} from "@heroicons/react/24/outline";
import { ActivityTypes } from "../constants/action-types";
import ActivityAction from "../actions/ActivityAction";
import { markAsDoneActivity } from "../api/ProjectApi";
import { useQuery } from "../AppLayout";
import { useParams } from "react-router-dom";
import AssignTo from "../board/components/AssignTo";
import { fromJS } from "immutable";
import { useTranslation } from "react-i18next";
import LayoutAction from "../actions/LayoutAction";
import { getBaseUrl } from "../api/api";

interface ActivityHoverCardProps {
  activity: any;
  isVisible: boolean;
  onClose: () => void;
  onSearch: (any) => void;
}

const ActivityHoverCard: React.FC<ActivityHoverCardProps> = ({
  activity,
  isVisible,
  onClose,
  onSearch,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { projectId } = useParams();
  const currentParams = useQuery();
  const contentType = currentParams.get("type") || "ACTIVITY";
  const pagination = useSelector((state: any) => state.taskReducer.getIn(["pagination"]));
  const taskFilters = useSelector((state: any) => state.taskReducer.getIn(["filters"]));
  const sortFields = useSelector((state: any) => state.taskReducer.getIn(["sorts"]));
  const [localDoneState, setLocalDoneState] = useState(activity?.event?.data?.done || false);
  const [localAssignees, setLocalAssignees] = useState(fromJS([]));

  const activityType = activity?.event?.type;
  const data = activity?.event?.data || {};
  // eslint-disable-next-line react-hooks/exhaustive-deps -- assignees used in effect
  const assignees = data.assignees || [];

  useEffect(() => {
    setLocalDoneState(data.done || false);
    setLocalAssignees(fromJS(assignees));
  }, [data.done, assignees]);

  const calendarIconUrl = getBaseUrl() + "/static/images/google-calendar.png";
  const gmailIconUrl = getBaseUrl() + "/static/images/gmail.png";

  const renderActivityIcon = () => {
    const iconClass = "w-5 h-5";
    if (data.source === "google") {
      return <img src={calendarIconUrl} className={iconClass} />;
    }
    if (
      activityType === ActivityTypes.sendEmail ||
      activityType === ActivityTypes.sendDraftEmail ||
      activityType === ActivityTypes.sendScheduledEmail ||
      activityType === ActivityTypes.recordSentEmail
    ) {
      if (data.hasGmail) {
        return <img src={gmailIconUrl} className={`w-8 h-8 object-contain`} />;
      }
      return <EnvelopeIcon className={iconClass} />;
    }
    switch (activityType) {
      case ActivityTypes.email:
      case ActivityTypes.logEmail:
      case ActivityTypes.sendEmail:
      case ActivityTypes.sendDraftEmail:
      case ActivityTypes.sendScheduledEmail:
      case ActivityTypes.recordSentEmail:
        return <EnvelopeIcon className={iconClass} />;
      case ActivityTypes.task:
        return <ClockIcon className={iconClass} />;
      case ActivityTypes.meeting:
        return <CalendarDaysIcon className={iconClass} />;
      case ActivityTypes.deadline:
        return <FlagIcon className={iconClass} />;
      case ActivityTypes.lunch:
        return <FaceSmileIcon className={iconClass} />;
      case ActivityTypes.call:
        return <PhoneIcon className={iconClass} />;
      default:
        return <CalendarDaysIcon className={iconClass} />;
    }
  };

  const formatTimeLabel = (value: any) => {
    if (!value) return "";
    if (typeof value === "string") {
      if (value.includes(":")) {
        const [hours, minutes] = value.split(":").slice(0, 2);
        const hour = parseInt(hours);
        const ampm = hour >= 12 ? "PM" : "AM";
        const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
        return `${displayHour}:${minutes} ${ampm}`;
      }
      return value;
    }
    try {
      return format(value, "h:mm a");
    } catch {
      return String(value);
    }
  };

  const handleEdit = () => {
    if (!activity?.id || !activity?.itemId) {
      return;
    }
    dispatch(ActivityAction.showActivityDialog(activity?.itemId, activity?.id));
    onClose();
  };

  const refreshActivityList = () => {
    if (projectId) {
      const searchRequest = {
        projectId,
        page: pagination.getIn(["number"]),
        pageSize: pagination.getIn(["size"]),
        filters: taskFilters.toJS(),
        types: [contentType],
        sorts: sortFields.toJS(),
        view: "CALENDAR",
      };
      onSearch(searchRequest);
    }
  };

  const handleMarkDone = () => {
    const newDoneState = !localDoneState;
    setLocalDoneState(newDoneState);

    markAsDoneActivity(activity?.itemId, activity?.id, newDoneState)
      .then(() => {
        refreshActivityList();
      })
      .catch(() => {
        setLocalDoneState(!newDoneState);
      });
  };

  const renderActivityType = () => {
    if (
      activityType === ActivityTypes.sendEmail ||
      activityType === ActivityTypes.sendDraftEmail ||
      activityType === ActivityTypes.sendScheduledEmail ||
      activityType === ActivityTypes.recordSentEmail
    ) {
      return data.hasGmail ? "Gmail" : "Email";
    }
    return activityType || "Meeting";
  };

  if (!isVisible || !activity) return null;

  return (
    <div className="fixed z-[9999] bg-white rounded-xl shadow-2xl border border-gray-200 p-6 max-w-sm w-full">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-blue-50 rounded-lg">{renderActivityIcon()}</div>
          <div>
            <h3 className="font-semibold text-gray-900 text-lg">{data.summary || "Activity"}</h3>
            <p className="text-sm text-gray-500 capitalize">{renderActivityType()}</p>
          </div>
        </div>
        <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg ml-1">
          <XMarkIcon className="w-5 h-5 text-gray-400" />
        </button>
      </div>

      {(data.startTime || data.endTime) && (
        <div className="mb-4 p-3 bg-gray-50 rounded-lg">
          <div className="flex items-center space-x-2 text-sm text-gray-700">
            <ClockIcon className="w-4 h-4" />
            <span className="font-medium">
              {formatTimeLabel(data.startTime)}
              {data.endTime && ` - ${formatTimeLabel(data.endTime)}`}
            </span>
          </div>
          {(data.start || data.end) && (
            <div className="flex items-center space-x-2 text-sm text-gray-600 mt-1">
              <CalendarDaysIcon className="w-4 h-4" />
              <span>
                {data.start &&
                  new Date(`${data.start}T00:00:00`).toLocaleDateString(undefined, {
                    weekday: "long",
                    month: "long",
                    day: "numeric",
                    year: "numeric",
                  })}
                {data.start && data.end && data.start !== data.end && (
                  <>
                    {" - "}
                    {new Date(`${data.end}T00:00:00`).toLocaleDateString(undefined, {
                      month: "long",
                      day: "numeric",
                      year: "numeric",
                    })}
                  </>
                )}
              </span>
            </div>
          )}
        </div>
      )}

      {data.assignees && data.assignees.length > 0 && (
        <div className="mb-4 p-3 bg-blue-50 rounded-lg">
          <div className="flex items-center space-x-2 text-sm text-gray-700 mb-2">
            <UserIcon className="w-4 h-4" />
            <span className="font-medium">{t("Owners")}</span>
          </div>
          <AssignTo
            assignees={localAssignees}
            sizeIcon={16}
            label={false}
            border={false}
            disabled={true}
          />
        </div>
      )}

      {data.source === "google" && (
        <div className="mb-4 p-3 bg-gray-50 rounded-lg">
          {(() => {
            const entryPoints = data.entryPoints || data.conferenceData?.entryPoints || [];
            const video = entryPoints.find((e: any) => e.entryPointType === "video") || {};
            const meetUri = video.uri || data.googleCalendarLink || data.hangoutLink;
            const meetLabel = video.label || (meetUri ? meetUri.replace(/^https?:\/\//, "") : "");
            const meetIcon = data.conferenceData?.conferenceSolution?.iconUri || calendarIconUrl;
            return (
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-2">
                  <img src={meetIcon} className="w-5 h-5 mt-1" alt="Meet" />
                  <div>
                    <a
                      href={meetUri}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center px-4 py-2 bg-[#1a73e8] text-white rounded-full text-sm font-medium hover:bg-[#1669c1]"
                    >
                      {t("Join with Google Meet")}
                    </a>
                    {meetLabel && (
                      <div
                        title={meetUri}
                        className="text-xs text-gray-600 mt-1 truncate max-w-[200px] cursor-default"
                      >
                        {meetLabel}
                      </div>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => {
                    if (!meetUri) return;
                    navigator.clipboard?.writeText(meetUri);
                    dispatch(
                      LayoutAction.setNotification({
                        title: t("Copied to clipboard"),
                        type: "success",
                        message: t("Google Meet link copied to clipboard"),
                      })
                    );
                  }}
                  className="p-2 hover:bg-gray-100 rounded-lg relative group"
                  title="Copy link"
                >
                  <ClipboardIcon className="w-5 h-5 text-gray-500" />
                  <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 text-xs text-white bg-gray-800 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-150 whitespace-nowrap z-[99999] pointer-events-none">
                    Copy link
                  </div>
                </button>
              </div>
            );
          })()}

          {(() => {
            const attendees: any[] = Array.isArray(data.attendees) ? data.attendees : [];
            const accepted = attendees.filter((a) => a.responseStatus === "accepted").length;
            const awaiting = attendees.filter((a) => a.responseStatus === "needsAction").length;
            const declined = attendees.filter((a) => a.responseStatus === "declined").length;
            const total = attendees.length;
            return (
              <div className="mt-4">
                <div className="flex items-center space-x-2 text-sm text-gray-800">
                  <UserIcon className="w-4 h-4" />
                  <span className="font-medium">{total} guests</span>
                </div>
                {(accepted > 0 || awaiting > 0 || declined > 0) && (
                  <div className="text-xs text-gray-500 mt-1">
                    {accepted > 0 && <span>{accepted} yes</span>}
                    {accepted > 0 && (awaiting > 0 || declined > 0) && <span>, </span>}
                    {awaiting > 0 && <span>{awaiting} awaiting</span>}
                    {awaiting > 0 && declined > 0 && <span>, </span>}
                    {declined > 0 && <span>{declined} no</span>}
                  </div>
                )}
                <div className="mt-2 space-y-2">
                  {attendees.map((att, idx) => (
                    <div key={idx} className="flex items-start space-x-2">
                      <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center text-xs font-semibold text-gray-700">
                        {(att.displayName || att.email || "?")
                          .toString()
                          .trim()
                          .charAt(0)
                          .toUpperCase()}
                      </div>
                      <div className="text-sm">
                        <div className="text-gray-800">
                          {att.email || att.displayName}
                          {att.organizer && (
                            <span className="ml-2 text-xs text-gray-500 align-middle">
                              Organizer
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}
        </div>
      )}

      {(activityType === ActivityTypes.sendEmail ||
        activityType === ActivityTypes.sendDraftEmail ||
        activityType === ActivityTypes.sendScheduledEmail ||
        activityType === ActivityTypes.recordSentEmail) && (
        <div className="mb-4 p-3 bg-red-50 rounded-lg">
          <div className="flex items-start space-x-2">
            {data.hasGmail ? (
              <img
                src={gmailIconUrl}
                className="w-5 h-5 mt-1 object-contain rounded p-0.5"
                alt="Gmail"
              />
            ) : (
              <EnvelopeIcon className="w-5 h-5 mt-1" />
            )}
            <div className="flex-1">
              {data.attendees && data.attendees.length > 0 && (
                <div className="space-y-1">
                  <div className="text-xs text-gray-600 font-medium">{t("To")}:</div>
                  <div className="space-y-1">
                    {data.attendees.map((email: string, idx: number) => (
                      <div key={idx} className="text-xs text-gray-700">
                        {email}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {data.description && (
                <div className="mt-2 max-w-full overflow-x-hidden">
                  {(() => {
                    const descriptionHtml = String(data.description || "").replace(/&nbsp;/g, " ");
                    return (
                      <div
                        className="text-xs text-gray-700 max-h-24 overflow-y-auto whitespace-pre-wrap break-words"
                        dangerouslySetInnerHTML={{ __html: descriptionHtml }}
                      />
                    );
                  })()}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {data.source !== "google" && (
        <div className="flex space-x-2">
          <button
            onClick={handleEdit}
            className="flex-1 flex items-center justify-center space-x-2 px-4 py-2 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors"
          >
            <span className="text-sm font-medium">View Details</span>
          </button>
          {activityType !== ActivityTypes.sendEmail &&
            activityType !== ActivityTypes.sendDraftEmail &&
            activityType !== ActivityTypes.sendScheduledEmail &&
            activityType !== ActivityTypes.recordSentEmail && (
              <button
                onClick={handleMarkDone}
                className={`flex-1 flex items-center justify-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
                  localDoneState
                    ? "bg-green-100 text-green-700 hover:bg-green-200"
                    : "bg-green-50 text-gray-400 hover:bg-green-100"
                }`}
              >
                <CheckCircleIcon
                  className={`w-6 cursor-pointer ${
                    localDoneState ? "text-green-600" : "text-gray-400"
                  }`}
                />
                <span className="text-sm font-medium">
                  {localDoneState ? t("Completed") : t("Mark as done")}
                </span>
              </button>
            )}
        </div>
      )}
    </div>
  );
};

export default ActivityHoverCard;
